import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getTiposUsuario, getUsuarios, resetearClaveDebug } from '../api/apiConfig'
import { useUser } from '../context/UserContext'
import {
  DEBUG_ROLE_PRESETS,
  PERMISSION_DEBUG_EMAILS,
  PERMISSION_SCREENS,
  buildRoleSimulationUser,
  buildUserSimulationUser,
  canUsePermissionDebug,
  evaluateScreens,
  userFromListRow,
} from '../utils/permissionDebug'

function pickTipoLabel(tipo) {
  if (typeof tipo === 'string') return tipo
  return String(tipo?.usuario || tipo?.TIPO_USUARIO || tipo?.tipo_usuario || tipo?.nombre || '').trim()
}

const OPERADOR_RESET_CLAVE = PERMISSION_DEBUG_EMAILS[0]

const DebugPermisos = () => {
  const navigate = useNavigate()
  const { realUser, user, setViewAs, clearViewAs, isViewAsActive, viewAs } = useUser()
  const [tab, setTab] = useState('rol')
  const [roles, setRoles] = useState([...DEBUG_ROLE_PRESETS])
  const [usuarios, setUsuarios] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [error, setError] = useState('')
  const [selectedRole, setSelectedRole] = useState(DEBUG_ROLE_PRESETS[0])
  const [userSearch, setUserSearch] = useState('')
  const [selectedUserEmail, setSelectedUserEmail] = useState('')
  const [previewUser, setPreviewUser] = useState(() => buildRoleSimulationUser(DEBUG_ROLE_PRESETS[0]))
  const [claveEmail, setClaveEmail] = useState('')
  const [claveNueva, setClaveNueva] = useState('')
  const [claveConfirma, setClaveConfirma] = useState('')
  const [claveOperador, setClaveOperador] = useState('')
  const [claveSaving, setClaveSaving] = useState(false)
  const [claveMessage, setClaveMessage] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoadingUsers(true)
        const [tiposRes, usersRes] = await Promise.all([
          getTiposUsuario().catch(() => []),
          getUsuarios().catch(() => []),
        ])
        if (cancelled) return

        const tiposArr = Array.isArray(tiposRes) ? tiposRes : tiposRes?.data || []
        const labels = [
          ...new Set(
            [...DEBUG_ROLE_PRESETS, ...tiposArr.map(pickTipoLabel)].filter(Boolean),
          ),
        ].sort((a, b) => a.localeCompare(b, 'es'))
        setRoles(labels)

        const usersArr = Array.isArray(usersRes) ? usersRes : usersRes?.data || []
        const mapped = usersArr.map(userFromListRow).filter(Boolean)
        mapped.sort((a, b) =>
          String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es', {
            sensitivity: 'base',
          }),
        )
        setUsuarios(mapped)
      } catch (e) {
        if (!cancelled) setError(e.message || 'No se pudo cargar usuarios/tipos')
      } finally {
        if (!cancelled) setLoadingUsers(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase()
    if (!q) return usuarios
    return usuarios.filter((u) => {
      const hay = `${u.nombre} ${u.email} ${u.tipo_usuario} ${u.grupo}`.toLowerCase()
      return hay.includes(q)
    })
  }, [usuarios, userSearch])

  const selectedUser = useMemo(
    () => usuarios.find((u) => u.email === selectedUserEmail) || null,
    [usuarios, selectedUserEmail],
  )

  const screens = useMemo(() => evaluateScreens(previewUser), [previewUser])
  const allowedCount = screens.filter((s) => s.allowed).length

  const matrixByRole = useMemo(() => {
    const rolesForMatrix = roles.length ? roles : [...DEBUG_ROLE_PRESETS]
    return rolesForMatrix.map((role) => ({
      role,
      screens: evaluateScreens(buildRoleSimulationUser(role)),
    }))
  }, [roles])

  const applyRolePreview = useCallback((role) => {
    setSelectedRole(role)
    setPreviewUser(buildRoleSimulationUser(role))
  }, [])

  const applyUserPreview = useCallback((user) => {
    if (!user) return
    setSelectedUserEmail(user.email)
    setPreviewUser(buildUserSimulationUser(user))
  }, [])

  const sesionDebug = canUsePermissionDebug(realUser || user)

  const handleResetClave = async (e) => {
    e.preventDefault()
    setClaveMessage('')
    if (!sesionDebug) {
      setClaveMessage('Solo el usuario de debug puede cambiar claves.')
      return
    }
    if (!claveEmail) {
      setClaveMessage('Selecciona un usuario.')
      return
    }
    if (claveNueva.length < 6) {
      setClaveMessage('La nueva clave debe tener al menos 6 caracteres.')
      return
    }
    if (claveNueva !== claveConfirma) {
      setClaveMessage('La confirmación no coincide.')
      return
    }
    if (!claveOperador) {
      setClaveMessage('Ingresa tu contraseña para autorizar el cambio.')
      return
    }
    try {
      setClaveSaving(true)
      await resetearClaveDebug({
        email: claveEmail,
        passwordNuevo: claveNueva,
        emailOperador: OPERADOR_RESET_CLAVE,
        passwordOperador: claveOperador,
      })
      setClaveNueva('')
      setClaveConfirma('')
      setClaveOperador('')
      setClaveMessage(`Contraseña actualizada para ${claveEmail}.`)
    } catch (err) {
      setClaveMessage(err.message || 'No se pudo cambiar la contraseña')
    } finally {
      setClaveSaving(false)
    }
  }

  const startSimulation = () => {
    if (!previewUser) return
    const mode = tab === 'rol' ? 'role' : 'user'
    setViewAs({
      mode,
      label:
        mode === 'role'
          ? selectedRole
          : selectedUser?.nombre || selectedUser?.email || previewUser.nombre,
      user: previewUser,
    })
    const first = evaluateScreens(previewUser).find((s) => s.allowed)
    navigate(first?.path || '/inicio')
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Debug de permisos</h1>
        <p className="mt-1 text-sm text-gray-600">
          Simula menú y acceso a pantallas por tipo de usuario o por usuario concreto, sin cerrar
          tu sesión. Solo disponible para depuración de permisos de frontend.
        </p>
      </div>

      {isViewAsActive && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <span>
            Simulación activa: <strong>{viewAs?.label}</strong>
          </span>
          <button
            type="button"
            onClick={clearViewAs}
            className="rounded bg-amber-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-900"
          >
            Salir de simulación
          </button>
        </div>
      )}

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-200">
        {[
          { id: 'rol', label: 'Por tipo de usuario' },
          { id: 'usuario', label: 'Por usuario' },
          { id: 'matriz', label: 'Matriz por rol' },
          { id: 'clave', label: 'Contraseñas' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'border-teal-600 text-teal-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'clave' && (
        <section className="max-w-lg space-y-4">
          <p className="text-sm text-gray-600">
            Cambia la clave de un usuario sin conocer la anterior. El servidor solo acepta a{' '}
            <strong>{OPERADOR_RESET_CLAVE}</strong> y pide tu contraseña vigente como autorización.
          </p>
          <form className="space-y-3" onSubmit={handleResetClave}>
            <label className="block text-sm font-medium text-gray-700">
              Buscar
              <input
                type="search"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="Nombre o correo"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Usuario
              <select
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={claveEmail}
                onChange={(e) => setClaveEmail(e.target.value)}
                required
              >
                <option value="">Selecciona…</option>
                {filteredUsers.map((u) => (
                  <option key={u.email} value={u.email}>
                    {u.nombre} — {u.email}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Nueva contraseña
              <input
                type="password"
                autoComplete="new-password"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={claveNueva}
                onChange={(e) => setClaveNueva(e.target.value)}
                minLength={6}
                required
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Confirmar contraseña
              <input
                type="password"
                autoComplete="new-password"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={claveConfirma}
                onChange={(e) => setClaveConfirma(e.target.value)}
                minLength={6}
                required
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Tu contraseña (autorización)
              <input
                type="password"
                autoComplete="current-password"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={claveOperador}
                onChange={(e) => setClaveOperador(e.target.value)}
                required
              />
            </label>
            {claveMessage && (
              <div
                className={`rounded border px-3 py-2 text-sm ${
                  claveMessage.startsWith('Contraseña actualizada')
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-red-200 bg-red-50 text-red-700'
                }`}
              >
                {claveMessage}
              </div>
            )}
            <button
              type="submit"
              disabled={claveSaving || loadingUsers}
              className="rounded-md bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {claveSaving ? 'Actualizando…' : 'Cambiar contraseña'}
            </button>
          </form>
        </section>
      )}

      {tab === 'rol' && (
        <section className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Tipo de usuario
            <select
              className="mt-1 w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={selectedRole}
              onChange={(e) => applyRolePreview(e.target.value)}
            >
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-gray-500">
            La simulación por rol no usa tu correo: evita heredar accesos por whitelist de email.
          </p>
        </section>
      )}

      {tab === 'usuario' && (
        <section className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Buscar usuario
            <input
              type="search"
              className="mt-1 w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="Nombre, email, rol, equipo…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          </label>
          {loadingUsers ? (
            <p className="text-sm text-gray-500">Cargando usuarios…</p>
          ) : (
            <div className="max-h-64 overflow-auto rounded-md border border-gray-200">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Nombre</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Tipo</th>
                    <th className="px-3 py-2">Equipo</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr
                      key={u.email}
                      onClick={() => applyUserPreview(u)}
                      className={`cursor-pointer border-t border-gray-100 hover:bg-teal-50 ${
                        selectedUserEmail === u.email ? 'bg-teal-50' : ''
                      }`}
                    >
                      <td className="px-3 py-2">{u.nombre}</td>
                      <td className="px-3 py-2 text-gray-600">{u.email}</td>
                      <td className="px-3 py-2">{u.tipo_usuario || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{u.grupo || '—'}</td>
                    </tr>
                  ))}
                  {!filteredUsers.length && (
                    <tr>
                      <td colSpan={4} className="px-3 py-4 text-center text-gray-500">
                        Sin coincidencias
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {(tab === 'rol' || tab === 'usuario') && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={startSimulation}
              disabled={!previewUser || (tab === 'usuario' && !selectedUser)}
              className="rounded-md bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Ver menú y pantallas como esta selección
            </button>
            <span className="text-sm text-gray-600">
              Acceso estimado: {allowedCount}/{screens.length} módulos
            </span>
          </div>

          <div className="overflow-auto rounded-md border border-gray-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Grupo</th>
                  <th className="px-3 py-2">Pantalla</th>
                  <th className="px-3 py-2">Ruta</th>
                  <th className="px-3 py-2">Acceso</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {screens.map((s) => (
                  <tr key={s.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-500">{s.group}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{s.label}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600">{s.path}</td>
                    <td className="px-3 py-2">
                      {s.allowed ? (
                        <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                          Sí
                        </span>
                      ) : (
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {s.allowed && (
                        <button
                          type="button"
                          className="text-xs text-teal-700 hover:underline"
                          onClick={() => {
                            setViewAs({
                              mode: tab === 'rol' ? 'role' : 'user',
                              label:
                                tab === 'rol'
                                  ? selectedRole
                                  : selectedUser?.nombre || selectedUser?.email,
                              user: previewUser,
                            })
                            navigate(s.path)
                          }}
                        >
                          Abrir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'matriz' && (
        <div className="overflow-auto rounded-md border border-gray-200">
          <table className="w-full min-w-[900px] text-left text-xs">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 px-2 py-2">Pantalla</th>
                {matrixByRole.map((col) => (
                  <th key={col.role} className="px-2 py-2 text-center font-medium text-gray-700">
                    <button
                      type="button"
                      className="hover:text-teal-700 hover:underline"
                      title={`Simular como ${col.role}`}
                      onClick={() => {
                        setTab('rol')
                        applyRolePreview(col.role)
                      }}
                    >
                      {col.role}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMISSION_SCREENS.map((screen) => (
                <tr key={screen.id} className="border-t border-gray-100">
                  <td className="sticky left-0 z-10 bg-white px-2 py-1.5 font-medium text-gray-800">
                    {screen.label}
                  </td>
                  {matrixByRole.map((col) => {
                    const ok = col.screens.find((s) => s.id === screen.id)?.allowed
                    return (
                      <td key={col.role} className="px-2 py-1.5 text-center">
                        {ok ? (
                          <span className="text-green-700">●</span>
                        ) : (
                          <span className="text-gray-300">·</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default DebugPermisos
